// Choisir un client en tapant son nom, son numéro ou son email.
//
// Toute la mécanique du champ vit dans ChoixListe ; ici, seulement ce qui est
// propre au client : ce qu'on cherche, et ce qu'on affiche sous son nom.

import { clientsTrouves, type ClientRecherchable } from '@imp/shared/recherche-client';
import { useCallback, useMemo } from 'react';

import { ChoixListe, type OptionChoix } from '@/components/ChoixListe';
import { type Contact, versRecherchable } from '@/modules/clients/api';

/** La deuxième ligne d'une proposition : de quoi distinguer deux homonymes. */
function detailClient(c: ClientRecherchable): string {
  return [c.telephone ?? c.telephoneMobile, c.email, c.codeComptable]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .join(' · ');
}

interface ChoixClientProps {
  /** Identifiant du champ, pour le <Label htmlFor>. */
  id?: string;
  clients: readonly Contact[];
  /** Identifiant du client choisi, chaîne vide si aucun. */
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Libellé de l'option « pas de client ». Absent : le choix est obligatoire. */
  labelAucun?: string;
  disabled?: boolean;
  /** La liste des clients n'est pas encore arrivée. */
  chargement?: boolean;
  /** Le champ est en erreur (client obligatoire non renseigné). */
  invalide?: boolean;
}

export function ChoixClient({
  id,
  clients,
  value,
  onChange,
  placeholder = 'Taper un nom, un numéro…',
  labelAucun,
  disabled = false,
  chargement = false,
  invalide = false,
}: ChoixClientProps) {
  const recherchables = useMemo(() => clients.map(versRecherchable), [clients]);

  const filtrer = useCallback(
    (saisie: string): OptionChoix[] =>
      clientsTrouves(recherchables, saisie).map((c) => ({
        id: c.id,
        nom: c.nom,
        detail: detailClient(c),
      })),
    [recherchables],
  );

  const choisi = useMemo((): OptionChoix | null => {
    const c = recherchables.find((x) => x.id === value);
    return c ? { id: c.id, nom: c.nom, detail: detailClient(c) } : null;
  }, [recherchables, value]);

  return (
    <ChoixListe
      {...(id === undefined ? {} : { id })}
      filtrer={filtrer}
      choisi={choisi}
      value={value}
      onChange={onChange}
      nomObjet="client"
      nomPluriel="clients"
      placeholder={placeholder}
      {...(labelAucun === undefined ? {} : { labelAucun })}
      disabled={disabled}
      chargement={chargement}
      aucunEnregistre={clients.length === 0}
      invalide={invalide}
    />
  );
}
